import { createPortal } from 'react-dom';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, Loader2, Sparkles, Square, X } from 'lucide-react';
import { tagsApi, transactionsApi } from '@/services/api';
import { invalidateExpenseQueries } from './tagQueries';

/** The AI lists merchants that fit a tag; the user ticks the ones to tag and applies them in one go. */
export function FindMatchesDialog({ tag, onClose }: { tag: { id: string; name: string }; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tag-matches', tag.id],
    queryFn: () => tagsApi.findMatches(tag.id),
    staleTime: Infinity,
    retry: false,
  });
  const matches = data?.data ?? [];
  // Until the user changes anything, the confident matches are ticked.
  const [choice, setChoice] = useState<Set<string> | null>(null);
  const picked = choice ?? new Set(matches.filter((m) => m.confidence >= 0.8).map((m) => m.merchantKey));

  const ids = matches.filter((m) => picked.has(m.merchantKey)).flatMap((m) => m.transactionIds);
  const apply = useMutation({
    mutationFn: async () => {
      for (let i = 0; i < ids.length; i += 500) await transactionsApi.bulk({ transactionIds: ids.slice(i, i + 500), addTagIds: [tag.id] });
    },
    onSuccess: () => { invalidateExpenseQueries(queryClient); onClose(); },
  });
  const toggle = (key: string) => {
    const next = new Set(picked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setChoice(next);
  };

  // Portaled: inside a space-y container the sibling margin would push the fixed overlay down.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div role="dialog" aria-label={`Find expenses for ${tag.name}`} className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-2xl border border-border bg-card shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="flex items-center gap-2 font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Expenses for “{tag.name}”</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-accent" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {isLoading && <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Looking through your merchants…</p>}
          {error && <p className="py-8 text-sm text-destructive">{error instanceof Error ? error.message : 'Could not ask the AI'}</p>}
          {!isLoading && !error && matches.length === 0 && <p className="py-8 text-sm text-muted-foreground">No merchants look like they belong here.</p>}
          <ul className="divide-y divide-border">
            {matches.map((m) => (
              <li key={m.merchantKey}>
                <button onClick={() => toggle(m.merchantKey)} className="flex w-full items-center gap-3 py-2.5 text-left">
                  {picked.has(m.merchantKey) ? <CheckSquare className="h-4 w-4 shrink-0 text-primary" /> : <Square className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  <span className="flex-1 truncate text-sm font-medium">{m.name}</span>
                  <span className="text-xs text-muted-foreground">{m.transactionIds.length} expense{m.transactionIds.length === 1 ? '' : 's'}</span>
                  <span className="w-12 text-right text-xs text-muted-foreground">{Math.round(m.confidence * 100)}%</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          {apply.error && <p className="mr-auto text-xs text-destructive">{apply.error instanceof Error ? apply.error.message : 'Could not tag'}</p>}
          <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent">Cancel</button>
          <button
            onClick={() => apply.mutate()}
            disabled={ids.length === 0 || apply.isPending}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {apply.isPending ? 'Tagging…' : `Tag ${ids.length} expense${ids.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
